import { For, Show, createEffect, createSignal } from "solid-js";

import { useCreateActivity, useDraftEmailForContact } from "~/lib/hooks";
import type { DraftEmailRequest } from "~/lib/types";

type Props = {
  wsKey: () => string;
  conId: () => string;
  contactLabel: () => string;
  // Optional anchors so the prompt knows the email is on behalf of an
  // account or deal. Both nullable; passed straight through to the API.
  contextAccountId?: () => string | null;
  contextDealId?: () => string | null;
  onClose: () => void;
};

type Stage = "purpose" | "draft" | "saved";

const TONES: DraftEmailRequest["tone"][] = ["professional", "friendly", "concise", "warm"];

/**
 * Two-stage HITL: rep states the purpose → AI drafts → rep reviews +
 * edits in place → save creates a planned outbound email Activity.
 *
 * The "rep is the gate" pattern: no admin review queue, the user
 * approves their own draft by editing + clicking Save. The original
 * LLM output is preserved on the AgentAction for provenance even if
 * the saved Activity diverges.
 */
export function DraftEmailModal(props: Props) {
  const [stage, setStage] = createSignal<Stage>("purpose");
  const [purpose, setPurpose] = createSignal("");
  const [tone, setTone] = createSignal<DraftEmailRequest["tone"]>("professional");
  const [subject, setSubject] = createSignal("");
  const [body, setBody] = createSignal("");
  const [model, setModel] = createSignal<string | null>(null);
  const [tokens, setTokens] = createSignal<{ in: number | null; out: number | null }>({
    in: null,
    out: null,
  });
  const [error, setError] = createSignal<string | null>(null);

  const draftMut = useDraftEmailForContact(props.wsKey);
  const createActivity = useCreateActivity(props.wsKey);

  // Re-focus the purpose textarea when first opening.
  let purposeRef: HTMLTextAreaElement | undefined;
  createEffect(() => {
    if (stage() === "purpose") purposeRef?.focus();
  });

  const runDraft = async () => {
    if (!purpose().trim()) return;
    setError(null);
    try {
      const r = await draftMut.mutateAsync({
        conId: props.conId(),
        body: {
          purpose: purpose().trim(),
          tone: tone() ?? undefined,
          context_account_id: props.contextAccountId?.() ?? undefined,
          context_deal_id: props.contextDealId?.() ?? undefined,
        },
      });
      setSubject(r.subject);
      setBody(r.body);
      setModel(r.model);
      setTokens({ in: r.input_tokens, out: r.output_tokens });
      setStage("draft");
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const save = async () => {
    if (!subject().trim() || !body().trim()) return;
    setError(null);
    try {
      await createActivity.mutateAsync({
        kind: "email",
        direction: "outbound",
        status: "planned",
        subject: subject().trim(),
        body: body(),
        primary_entity_type: "contact",
        primary_entity_id: props.conId(),
      });
      setStage("saved");
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="draft-email-title">
      <div class="modal-box max-w-2xl">
        <h3 id="draft-email-title" class="font-bold text-lg mb-2">
          ✨ Draft email to {props.contactLabel()}
        </h3>

        <Show when={error()}>
          <div role="alert" class="alert alert-error text-sm mb-3">
            {error()}
          </div>
        </Show>

        <Show when={stage() === "purpose"}>
          <div class="flex flex-col gap-3">
            <div>
              <label class="label" for="email-purpose">
                <span class="label-text">What's the email about?</span>
              </label>
              <textarea
                ref={purposeRef}
                id="email-purpose"
                class="textarea textarea-bordered w-full"
                rows={3}
                placeholder="e.g. Follow up on yesterday's demo and share the pricing PDF."
                value={purpose()}
                onInput={(e) => setPurpose(e.currentTarget.value)}
              />
            </div>
            <div>
              <label class="label" for="email-tone">
                <span class="label-text">Tone</span>
              </label>
              <select
                id="email-tone"
                class="select select-bordered select-sm"
                value={tone() ?? "professional"}
                onChange={(e) => setTone(e.currentTarget.value as DraftEmailRequest["tone"])}
              >
                <For each={TONES}>{(t) => <option value={t ?? ""}>{t}</option>}</For>
              </select>
            </div>
          </div>
          <div class="modal-action">
            <button type="button" class="btn btn-ghost" onClick={props.onClose}>
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              onClick={runDraft}
              disabled={!purpose().trim() || draftMut.isPending}
            >
              {draftMut.isPending ? "Drafting…" : "Generate draft"}
            </button>
          </div>
        </Show>

        <Show when={stage() === "draft"}>
          <div class="flex flex-col gap-3">
            <div>
              <label class="label" for="email-subject">
                <span class="label-text">Subject</span>
              </label>
              <input
                id="email-subject"
                type="text"
                class="input input-bordered w-full"
                value={subject()}
                onInput={(e) => setSubject(e.currentTarget.value)}
              />
            </div>
            <div>
              <label class="label" for="email-body">
                <span class="label-text">Body</span>
              </label>
              <textarea
                id="email-body"
                class="textarea textarea-bordered w-full font-sans"
                rows={12}
                value={body()}
                onInput={(e) => setBody(e.currentTarget.value)}
              />
            </div>
            <p class="text-xs opacity-50">
              Edit freely. Saving creates a <strong>planned</strong> outbound email activity —
              nothing is sent.
              <Show when={model()}>
                <span>
                  {" "}
                  · model: <code>{model()}</code>
                </span>
              </Show>
              <Show when={tokens().in !== null || tokens().out !== null}>
                <span>
                  {" "}
                  · {tokens().in ?? "?"} in / {tokens().out ?? "?"} out tokens
                </span>
              </Show>
            </p>
          </div>
          <div class="modal-action">
            <button type="button" class="btn btn-ghost" onClick={() => setStage("purpose")}>
              Back
            </button>
            <button type="button" class="btn" onClick={runDraft} disabled={draftMut.isPending}>
              {draftMut.isPending ? "Regenerating…" : "Regenerate"}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              onClick={save}
              disabled={!subject().trim() || !body().trim() || createActivity.isPending}
            >
              {createActivity.isPending ? "Saving…" : "Save draft"}
            </button>
          </div>
        </Show>

        <Show when={stage() === "saved"}>
          <div class="py-6 text-center">
            <p class="text-success font-medium">
              ✓ Saved as a planned email activity on the contact.
            </p>
            <p class="text-xs opacity-60 mt-2">
              You can find it in the contact's timeline and edit or send it from there.
            </p>
          </div>
          <div class="modal-action">
            <button type="button" class="btn btn-primary" onClick={props.onClose}>
              Done
            </button>
          </div>
        </Show>
      </div>
    </dialog>
  );
}
